import { useState, useEffect, useRef } from 'react';
import { doc, getDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAdminRole } from './useAdminRole';
import { AppSection, SectionAccessConfig, AppConfiguration } from '../types/admin';

/**
 * Hook pour vérifier l'accès aux sections de l'application
 *
 * Vérifie si une section est activée et si l'utilisateur a le rôle requis
 *
 * Usage:
 * ```tsx
 * const { hasAccess, loading } = useSectionAccess('applications');
 *
 * if (loading) return <Skeleton />;
 * if (!hasAccess) return <Navigate to="/dashboard" />;
 *
 * return <ApplicationsPage />;
 * ```
 */
export const useSectionAccess = (section: AppSection) => {
  const { role, loading: roleLoading } = useAdminRole();
  const [hasAccess, setHasAccess] = useState(true); // Par défaut, accès autorisé
  const [hiddenInNav, setHiddenInNav] = useState(false); // Par défaut, visible dans le menu
  const [comingSoon, setComingSoon] = useState(false); // Par défaut, pas en mode "bientôt"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const isMountedRef = useRef(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Tant que le rôle utilisateur est en cours de chargement, ne pas refuser l'accès.
  // Cela évite les redirections "flash" vers l'accueil sur les routes protégées par section.
  useEffect(() => {
    if (roleLoading) {
      setLoading(true);
      // Ne pas modifier hasAccess pendant le chargement du rôle pour éviter les faux négatifs
    }
  }, [roleLoading]);


  useEffect(() => {
    isMountedRef.current = true;

    const configDocRef = doc(db, 'appConfiguration', 'main');

    const unsubscribe = onSnapshot(
      configDocRef,
      (docSnap) => {
        if (!isMountedRef.current) return;

        // Si le rôle est encore en cours de chargement, ne pas prendre de décision d'accès
        // Cela évite les flashes "Accès refusé" transitoires
        if (roleLoading) {
          // Garder loading=true et ne pas modifier hasAccess
          return;
        }

        try {
          if (!docSnap.exists()) {
            // Si pas de configuration, accès autorisé par défaut
            setHasAccess(true);
            setHiddenInNav(false);
            setComingSoon(false);
            setLoading(false);
            return;
          }

          const config = docSnap.data() as AppConfiguration;
          const sectionConfig = config.sections?.[section];

          if (!sectionConfig) {
            // Si la section n'existe pas dans la config, accès autorisé par défaut
            setHasAccess(true);
            setHiddenInNav(false);
            setComingSoon(false);
            setLoading(false);
            return;
          }

          // Mettre à jour hiddenInNav (pour le menu)
          setHiddenInNav(sectionConfig.hiddenInNav ?? false);
          // Mettre à jour comingSoon
          setComingSoon(sectionConfig.comingSoon ?? false);

          // Vérifier si la section est activée
          if (!sectionConfig.enabled) {
            // Utiliser // Debug au lieu de // Warning car c'est un comportement attendu
            // Les sections peuvent être intentionnellement désactivées par l'admin
            // Debug(`[useSectionAccess] Section ${section} est désactivée dans la configuration. Accès refusé.`);
            setHasAccess(false);
            setLoading(false);
            return;
          }

          // Vérifier si le rôle de l'utilisateur est autorisé
          // S'assurer que allowedRoles existe et est un tableau
          const allowedRoles = sectionConfig.allowedRoles || [];

          // Si allowedRoles est vide/absent dans Firestore, on considère que la section est ouverte
          // (sinon une config partielle bloquerait involontairement tous les utilisateurs).
          if (allowedRoles.length === 0) {
            setHasAccess(true);
            setLoading(false);
            return;
          }

          const isRoleAllowedDirect = allowedRoles.includes(role);
          // Cohérence avec l'app: support/finance/tech ont l'accès admin panel.
          // Si une section autorise "admin", on autorise aussi ces rôles.
          const isAdminEquivalentRole = role === 'support' || role === 'finance' || role === 'tech';
          const isRoleAllowedByAdminEquivalence =
            isAdminEquivalentRole && allowedRoles.includes('admin');
          const isRoleAllowed = isRoleAllowedDirect || isRoleAllowedByAdminEquivalence;

          setHasAccess(isRoleAllowed);
          setLoading(false);
        } catch (err: any) {
          if (!isMountedRef.current) return;
          // Error('Error checking section access:', err);
          setError(err.message || 'Erreur lors de la vérification de l\'accès');
          // En cas d'erreur, autoriser l'accès par défaut pour ne pas bloquer l'utilisateur
          setHasAccess(true);
          setLoading(false);
        }
      },
      (err) => {
        if (!isMountedRef.current) return;
        // Error('Firestore error:', err);
        setError('Erreur de connexion. Veuillez réessayer.');
        // En cas d'erreur, autoriser l'accès par défaut
        setHasAccess(true);
        setLoading(false);
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        try {
          unsubscribeRef.current();
        } catch (err) {
          // Ignorer les erreurs de cleanup si Firestore est déjà fermé
          // Warning('Error during cleanup (ignored):', err);
        }
        unsubscribeRef.current = null;
      }
    };
  }, [section, role, roleLoading]);

  return {
    hasAccess,
    hiddenInNav, // Si true, masquer dans le menu mais permettre l'accès via URL
    comingSoon, // Si true, afficher un placeholder "Bientôt disponible" au lieu du contenu
    loading,
    error,
    role, // Exposer le rôle pour éviter le double fetch dans ProtectedSectionRoute
  };
};

/**
 * Hook pour récupérer toute la configuration des sections
 */
export const useAppConfiguration = () => {
  const [config, setConfig] = useState<AppConfiguration | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const isMountedRef = useRef(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    const configDocRef = doc(db, 'appConfiguration', 'main');

    const unsubscribe = onSnapshot(
      configDocRef,
      (docSnap) => {
        if (!isMountedRef.current) return;

        try {
          if (!docSnap.exists()) {
            // Configuration par défaut si elle n'existe pas
            const defaultConfig: AppConfiguration = {
              id: 'main',
              sections: {
                job_search: {
                  section: 'job_search',
                  enabled: true,
                  hiddenInNav: false,
                  // Section ouverte à tous les users authentifiés. Les capacités
                  // varient selon le tier : free voit la liste en lecture seule,
                  // trial/premium peuvent éditer leurs critères + activer l'autopilote
                  // (gating géré au niveau des composants Suggestions/Autopilote, pas de la route).
                  allowedRoles: ['user', 'admin', 'support', 'finance', 'tech'],
                  description: 'Recherche d\'emploi - Recherche et filtrage d\'offres',
                },
                cv_builder: {
                  section: 'cv_builder',
                  enabled: true,
                  hiddenInNav: false,
                  allowedRoles: ['user', 'admin', 'support', 'finance', 'tech'],
                  description: 'Mes CV - Gestion et édition de CVs',
                },
                applications: {
                  section: 'applications',
                  enabled: true,
                  hiddenInNav: false,
                  allowedRoles: ['user', 'admin', 'support', 'finance', 'tech'],
                  description: 'Mes Candidatures - Suivi des candidatures',
                },
                coaching: {
                  section: 'coaching',
                  enabled: true,
                  hiddenInNav: false,
                  comingSoon: true,
                  allowedRoles: ['user', 'admin', 'support', 'coach'],
                  description: 'Sessions de coaching et réservation',
                },
              },
              updatedAt: Timestamp.now(),
              updatedBy: 'system',
            };
            setConfig(defaultConfig);
            setLoading(false);
            return;
          }

          const data = docSnap.data() as AppConfiguration;

          // Normaliser la configuration : s'assurer que toutes les sections sont présentes
          // Si une section manque dans Firestore, elle sera ajoutée avec les valeurs par défaut
          const normalizedSections: Record<AppSection, SectionAccessConfig> = {
            job_search: data.sections?.job_search || {
              section: 'job_search',
              enabled: true,
              hiddenInNav: false,
              // Section ouverte à tous (gating fin par tier dans les composants).
              allowedRoles: ['user', 'admin', 'support', 'finance', 'tech'],
              description: 'Recherche d\'emploi - Recherche et filtrage d\'offres',
            },
            cv_builder: data.sections?.cv_builder || {
              section: 'cv_builder',
              enabled: true,
              hiddenInNav: false,
              allowedRoles: ['user', 'admin', 'support', 'finance', 'tech'],
              description: 'Mes CV - Gestion et édition de CVs',
            },
            applications: data.sections?.applications || {
              section: 'applications',
              enabled: true,
              hiddenInNav: false,
              allowedRoles: ['user', 'admin', 'support', 'finance', 'tech'],
              description: 'Mes Candidatures - Suivi des candidatures',
            },
            coaching: data.sections?.coaching || {
              section: 'coaching',
              enabled: true,
              hiddenInNav: false,
              allowedRoles: ['user', 'admin', 'support', 'coach'],
              description: 'Sessions de coaching et réservation',
            },
          };

          setConfig({
            ...data,
            id: docSnap.id,
            sections: normalizedSections,
          });
          setLoading(false);
        } catch (err: any) {
          if (!isMountedRef.current) return;
          // Error('Error fetching app configuration:', err);
          setError(err.message || 'Erreur lors du chargement de la configuration');
          setLoading(false);
        }
      },
      (err) => {
        if (!isMountedRef.current) return;
        // Error('Firestore error:', err);
        setError('Erreur de connexion. Veuillez réessayer.');
        setLoading(false);
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        try {
          unsubscribeRef.current();
        } catch (err) {
          // Ignorer les erreurs de cleanup si Firestore est déjà fermé
          // Warning('Error during cleanup (ignored):', err);
        }
        unsubscribeRef.current = null;
      }
    };
  }, []);

  return {
    config,
    loading,
    error,
  };
};

/**
 * Hook pour vérifier si le dashboard coach est activé dans le menu
 *
 * Usage:
 * ```tsx
 * const { coachDashboardEnabled, loading } = useCoachDashboardMenu();
 *
 * if (loading) return <Skeleton />;
 * if (coachDashboardEnabled && isCoach) {
 *   // Afficher le menu coach
 * }
 * ```
 */
export const useCoachDashboardMenu = () => {
  const [coachDashboardEnabled, setCoachDashboardEnabled] = useState<boolean>(true); // Par défaut activé
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const isMountedRef = useRef(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    const configDocRef = doc(db, 'appConfiguration', 'main');

    const unsubscribe = onSnapshot(
      configDocRef,
      (docSnap) => {
        if (!isMountedRef.current) return;

        try {
          if (!docSnap.exists()) {
            // Si pas de configuration, dashboard coach activé par défaut
            setCoachDashboardEnabled(true);
            setLoading(false);
            return;
          }

          const data = docSnap.data() as AppConfiguration;
          setCoachDashboardEnabled(data.coachDashboardEnabled ?? true); // Par défaut activé
          setLoading(false);
        } catch (err: any) {
          if (!isMountedRef.current) return;
          // Error('Error checking coach dashboard menu status:', err);
          setError(err.message || 'Erreur lors de la vérification du menu coach');
          // En cas d'erreur, activer par défaut
          setCoachDashboardEnabled(true);
          setLoading(false);
        }
      },
      (err) => {
        if (!isMountedRef.current) return;
        // Error('Firestore error:', err);
        setError('Erreur de connexion. Veuillez réessayer.');
        // En cas d'erreur, activer par défaut
        setCoachDashboardEnabled(true);
        setLoading(false);
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        try {
          unsubscribeRef.current();
        } catch (err) {
          // Ignorer les erreurs de cleanup si Firestore est déjà fermé
          // Warning('Error during cleanup (ignored):', err);
        }
        unsubscribeRef.current = null;
      }
    };
  }, []);

  return {
    coachDashboardEnabled,
    loading,
    error,
  };
};

/**
 * Hook pour vérifier si l'onglet Coaching Vocal est activé
 *
 * Usage:
 * ```tsx
 * const { voiceCoachingEnabled, loading } = useVoiceCoachingEnabled();
 *
 * if (!voiceCoachingEnabled) {
 *   // Ne pas afficher l'onglet Coaching Vocal
 * }
 * ```
 */
export const useVoiceCoachingEnabled = () => {
  const [voiceCoachingEnabled, setVoiceCoachingEnabled] = useState<boolean>(true); // Par défaut activé
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const isMountedRef = useRef(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    const configDocRef = doc(db, 'appConfiguration', 'main');

    const unsubscribe = onSnapshot(
      configDocRef,
      (docSnap) => {
        if (!isMountedRef.current) return;

        try {
          if (!docSnap.exists()) {
            setVoiceCoachingEnabled(true);
            setLoading(false);
            return;
          }

          const data = docSnap.data() as AppConfiguration;
          setVoiceCoachingEnabled(data.voiceCoachingEnabled ?? true);
          setLoading(false);
        } catch (err: any) {
          if (!isMountedRef.current) return;
          setError(err.message || 'Erreur lors de la vérification du coaching vocal');
          setVoiceCoachingEnabled(true);
          setLoading(false);
        }
      },
      (err) => {
        if (!isMountedRef.current) return;
        setError('Erreur de connexion. Veuillez réessayer.');
        setVoiceCoachingEnabled(true);
        setLoading(false);
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        try {
          unsubscribeRef.current();
        } catch (err) {
          // Ignorer les erreurs de cleanup
        }
        unsubscribeRef.current = null;
      }
    };
  }, []);

  return {
    voiceCoachingEnabled,
    loading,
    error,
  };
};

/**
 * Hook pour récupérer le thème global de l'application depuis Firestore
 *
 * Le thème est défini par l'administrateur et s'applique à TOUS les utilisateurs.
 * Par défaut: 'warm-success' en mode 'light'
 *
 * Usage:
 * ```tsx
 * const { globalTheme, colorMode, loading } = useGlobalTheme();
 * ```
 */
export const useGlobalTheme = () => {
  const [globalTheme, setGlobalTheme] = useState<string>('warm-success');
  const [colorMode, setColorMode] = useState<'light' | 'dark'>('light');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const isMountedRef = useRef(true);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    isMountedRef.current = true;

    const configDocRef = doc(db, 'appConfiguration', 'main');
    // Log('[useGlobalTheme] Setting up Firestore listener...');

    const unsubscribe = onSnapshot(
      configDocRef,
      (docSnap) => {
        if (!isMountedRef.current) return;

        try {
          if (!docSnap.exists()) {
            // Valeurs par défaut si pas de configuration
            // Log('[useGlobalTheme] No config doc, using defaults');
            setGlobalTheme('warm-success');
            setColorMode('light');
            setLoading(false);
            return;
          }

          const data = docSnap.data() as AppConfiguration;
          setGlobalTheme(data.globalTheme || 'warm-success');
          setColorMode(data.globalColorMode || 'light');
          setLoading(false);
        } catch (err: any) {
          if (!isMountedRef.current) return;
          // Error('Error fetching global theme:', err);
          setError(err.message || 'Erreur lors du chargement du thème');
          // Valeurs par défaut en cas d'erreur
          setGlobalTheme('warm-success');
          setColorMode('light');
          setLoading(false);
        }
      },
      (err) => {
        if (!isMountedRef.current) return;
        // Error('Firestore error:', err);
        setError('Erreur de connexion. Veuillez réessayer.');
        // Valeurs par défaut en cas d'erreur
        setGlobalTheme('warm-success');
        setColorMode('light');
        setLoading(false);
      }
    );

    unsubscribeRef.current = unsubscribe;

    return () => {
      isMountedRef.current = false;
      if (unsubscribeRef.current) {
        try {
          unsubscribeRef.current();
        } catch (err) {
          // Warning('Error during cleanup (ignored):', err);
        }
        unsubscribeRef.current = null;
      }
    };
  }, []);

  return {
    globalTheme,
    colorMode,
    loading,
    error,
  };
};
